import React, { useState, useCallback, useEffect } from "react";
import { Form, Button, message, Spin } from "antd";
import PersonalInfoSection from "./PersonalInfoSection";
import BeneficiarySection from "./BeneficiarySection";
import GolfHistorySection from "./GolfHistorySection";
import EquipmentSection from "./EquipmentSection";
import CoverageSummarySection from "./CoverageSummarySection";
import InsurancePeriodSection from "./InsurancePeriodSection";
import ApplicationList from "./ApplicationList";
import insuranceApi from "api/insuranceApi";
import { transformFormValues, isTimeoutError } from "./formTransform";

const VIEW_STATES = {
  LOADING: "loading",
  LIST: "list",
  FORM: "form",
};

/**
 * InsuranceApplicationForm — main container that composes all form sections,
 * handles validation, submission, and error/success states.
 * Implements 3-state page flow: loading → list (if apps exist) or form (if none).
 *
 * Requirements: 1.1, 1.2, 1.3, 9.1, 9.2, 9.3, 9.4, 9.5, 10.1, 10.5
 */
export default function InsuranceApplicationForm() {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [submitting, setSubmitting] = useState(false);
  const [viewState, setViewState] = useState(VIEW_STATES.LOADING);
  const [applications, setApplications] = useState([]);

  // On mount: fetch existing applications to determine initial view
  useEffect(() => {
    async function fetchApplications() {
      try {
        const response = await insuranceApi.getMyApplications();
        const apps = response?.data?.data || [];
        if (apps.length > 0) {
          setApplications(apps);
          setViewState(VIEW_STATES.LIST);
        } else {
          setViewState(VIEW_STATES.FORM);
        }
      } catch {
        // Graceful fallback — show form on any error (401, network, etc.)
        setViewState(VIEW_STATES.FORM);
      }
    }
    fetchApplications();
  }, []);

  const handleBuyAnother = useCallback(() => {
    setViewState(VIEW_STATES.FORM);
  }, []);

  const handleCancelForm = useCallback(() => {
    setViewState(VIEW_STATES.LIST);
  }, []);

  const handleSubmit = useCallback(
    async (values) => {
      if (submitting) return;

      setSubmitting(true);

      try {
        const requestBody = transformFormValues(values);
        const createResponse = await insuranceApi.createApplication(requestBody);
        const createdData = createResponse?.data?.data || {};

        // After successful submission, re-fetch applications and show list
        try {
          const response = await insuranceApi.getMyApplications();
          const apps = response?.data?.data || [];
          setApplications(apps);
          setViewState(VIEW_STATES.LIST);
        } catch {
          // If re-fetch fails, optimistically insert the new application
          const optimisticApp = {
            referenceNumber: createdData.referenceNumber || "",
            status: "waiting_for_payment",
            submittedAt: createdData.submittedAt || new Date().toISOString(),
            insuranceStartDate: values.insuranceStartDate
              ? values.insuranceStartDate.toISOString()
              : null,
          };
          setApplications((prev) => [optimisticApp, ...prev]);
          setViewState(VIEW_STATES.LIST);
        }
      } catch (error) {
        if (isTimeoutError(error)) {
          messageApi.error(
            "การส่งข้อมูลใช้เวลานานเกินกำหนด กรุณาลองใหม่อีกครั้ง"
          );
        } else {
          messageApi.error(
            "เกิดข้อผิดพลาดในการส่งข้อมูล กรุณาลองใหม่อีกครั้ง"
          );
        }
      } finally {
        setSubmitting(false);
      }
    },
    [submitting, messageApi]
  );

  const handleSubmitFailed = useCallback(() => {
    messageApi.error("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
  }, [messageApi]);

  // Loading state — show spinner
  if (viewState === VIEW_STATES.LOADING) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 300,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // List state — show ApplicationList
  if (viewState === VIEW_STATES.LIST) {
    return (
      <>
        {contextHolder}
        <ApplicationList
          applications={applications}
          onBuyAnother={handleBuyAnother}
        />
      </>
    );
  }

  // Form state — show insurance application form
  return (
    <div style={{ padding: "16px", maxWidth: 800, margin: "0 auto" }}>
      {contextHolder}
      {applications.length > 0 && (
        <Button
          type="link"
          onClick={handleCancelForm}
          style={{ padding: 0, marginBottom: 8 }}
        >
          ← กลับไปรายการ (Back to list)
        </Button>
      )}
      <h2>ใบสมัครประกันภัยกอล์ฟ (Golfer Insurance Application)</h2>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onFinishFailed={handleSubmitFailed}
        scrollToFirstError
        preserve
      >
        <PersonalInfoSection />
        <BeneficiarySection />
        <GolfHistorySection />
        <EquipmentSection />
        <CoverageSummarySection />
        <InsurancePeriodSection />

        <Form.Item style={{ marginTop: 24 }}>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            block
            loading={submitting}
            disabled={submitting}
          >
            {submitting ? "กำลังส่งข้อมูล..." : "ส่งใบสมัคร (Submit)"}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
}
